import { useCallback, useEffect, useState } from "react";

import type { ProductEntry, ProductJourney } from "../../analytics/productEvents";
import type { SelectOption } from "../../utils/tokenHelpers";
import type { SwapView, UseSwapReturn } from "../../widgets/types/swap";
import { BUY_SCHEDULE, MOCK_BUY_RECEIPT_ROWS, MOCK_SELL_RECEIPT_ROWS, SELL_SCHEDULE } from "../schedules";
import { useSimMode } from "../SimModeContext";
import { useSimulatedTransaction } from "../useSimulatedTransaction";

export interface SwapOrchestrationOptions {
    entry_point?: ProductEntry;
    analytics?: ProductJourney;
    /** Production ignores this visual simulation input. */
    simSwapSide?: "buy" | "sell";
}

declare module "../SimModeContext" {
    interface SimRealHooks {
        useSwap?: (options: SwapOrchestrationOptions) => UseSwapReturn;
    }
}

/**
 * Selects between the real `useSwap` (injected via SimModeProvider's
 * `realHooks`) and a simulated implementation driven by phase
 * schedules.
 *
 * **Rules-of-hooks contract:** the choice is made on
 * `sim.enabled` at render time. This is safe iff the provider's
 * `scenario` value is stable for the widget's lifetime. The
 * `SimModeProvider` documentation describes that contract; callers
 * that want to toggle scenarios should remount the subtree via
 * `key={scenario}`.
 *
 * @example apps/main wiring
 * ```tsx
 * // App.tsx
 * const realHooks = useMemo(() => ({ useSwap }), []);
 * <SimModeProvider realHooks={realHooks}>
 *   <App />
 * </SimModeProvider>
 *
 * // BuyWidget.tsx
 * const swap = useSwapOrchestration({});
 * ```
 *
 * @example external consumer
 * ```tsx
 * <SimModeProvider scenario="happy" wallet={mockWallet}>
 *   <BuyWidget />
 * </SimModeProvider>
 * ```
 */
export function useSwapOrchestration(args: SwapOrchestrationOptions = {}): UseSwapReturn {
    const sim = useSimMode();
    const useReal = sim.realHooks?.useSwap;
    const useImplementation = sim.enabled ? useSimulatedSwap : useReal;
    if (!useImplementation) {
        throw new Error(
            "useSwapOrchestration: no real hook injected and sim mode is off. " +
                "Either mount <SimModeProvider realHooks={{ useSwap }}> at the app root, " +
                "or pass scenario='happy' / 'error' for sim mode."
        );
    }
    return useImplementation(args);
}

// ── Simulated implementation ───────────────────────────────────

const MOCK_USDC: SelectOption = {
    label: "USDC",
    value: "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v"
};
const MOCK_USDV: SelectOption = {
    label: "USDv",
    value: "USDvSimMockMintAddress11111111111111111111111"
};

/**
 * Sim version of `useSwap`. Walks the BUY/SELL phase schedules and
 * returns a `UseSwapReturn`-shaped object with mock display values.
 *
 * Handlers are mostly no-ops since sim mode is scripted; only
 * `handleSubmit` triggers the timer, `handlePendingCancel` /
 * `handleSuccessClose` reset.
 */
// Reasonable form-view height for the BuyWidget / Swap content at
// `max-w-md` / `max-w-xl`. Production captures the actual DOM
// `cardRef.current?.offsetHeight` when the user clicks submit; in
// sim mode the auto-trigger bypasses the widget's submit handler
// (it fires `execute()` directly on the orchestrator) so we don't
// have a measurement opportunity. Using a fixed value here keeps
// the card height-locked during pending+ so the receipt expand +
// input collapse stay inside a stable bounding box. Tweak per
// widget breakpoint if the visual feels off.
const SIM_BUY_CARD_HEIGHT = 480;

function useSimulatedSwap(args: SwapOrchestrationOptions): UseSwapReturn {
    const sim = useSimMode();
    const side = args.simSwapSide ?? "buy";
    const isSell = side === "sell";

    // Token-direction fields keyed off `side`. Buy: send USDC →
    // receive USDv. Sell: send USDv → receive USDC. All amounts stay
    // 1:1 at 10,000 regardless of side.
    const sendToken = isSell ? MOCK_USDV : MOCK_USDC;
    const receiveToken = isSell ? MOCK_USDC : MOCK_USDV;
    const sendSymbol = sendToken.label;
    const receiveSymbol = receiveToken.label;
    // Receipt-row "Price" string carries the receive-token symbol.
    // Reuse the existing per-side mock rows for impact/fee/network.
    const receiptRows = isSell ? MOCK_SELL_RECEIPT_ROWS : MOCK_BUY_RECEIPT_ROWS;

    const scenario = sim.scenario === "off" ? "happy" : sim.scenario;
    const { phase, state, execute, reset } = useSimulatedTransaction({
        scenario,
        schedule: isSell ? SELL_SCHEDULE : BUY_SCHEDULE,
        repeatPauseMs: sim.repeatPauseMs
    });
    // Track a sim card-height so the widget can apply its
    // `{height, overflow:hidden}` lock during pending+. We seed it
    // from `SIM_BUY_CARD_HEIGHT` once we leave the form view; the
    // widget then locks at that height through the receipt and
    // success phases, matching production's height-stable receipt
    // expand pattern.
    const [cardHeight, setCardHeight] = useState<number | undefined>(undefined);

    const handleSubmit = useCallback(
        async (measured?: number): Promise<void> => {
            // Honor a measured height if the widget passes one (production
            // path); otherwise fall back to the sim constant.
            setCardHeight(measured ?? SIM_BUY_CARD_HEIGHT);
            await execute();
        },
        [execute]
    );
    const noop = useCallback(() => undefined, []);
    const noopValueSet = useCallback((_v: string): void => undefined, []);
    const noopOptionSet = useCallback((): void => undefined, []);

    const isPendingOrLater =
        phase === "pending" ||
        phase === "receipt" ||
        phase === "processing" ||
        phase === "success" ||
        phase === "postSuccess";
    // Phase-gating contract — modeled on production keystroke flow:
    //   empty   → nothing rendered: both amounts blank, no balance row,
    //             no FeeFooter
    //   focused → input is tapped: balance row revealed, exchange-rate
    //             chip still shown (cached price), values still blank
    //   filled  → input has a value: send amount + usdValue immediate,
    //             quote-derived fields (receiveAmount, priceImpact,
    //             routingFee) stream in over ~700ms (see filledStage
    //             effect below)
    //   pending+ → snapshot values from `swapSnapshot` take over via
    //             the widget's `isPendingOrLater` branch.
    const inputHasValue = phase === "filled" || isPendingOrLater;
    const isFocusedOrLater = phase !== "empty";

    // ── Scripted amount typing ────────────────────────────────────────
    // The full send amount ("10000"). When the `filled` phase begins, the
    // sim "types" it in character by character so the input PLAYS the
    // keystrokes instead of snapping the whole value in. Once we're at
    // pending or later the snapshot path is authoritative, so the full
    // value is reported regardless of the typing progress.
    const FULL_SEND_AMOUNT = "10000";
    const TYPE_CHAR_MS = 45;
    const [typedChars, setTypedChars] = useState(0);
    useEffect(() => {
        if (phase !== "filled") {
            // Reset to 0 in `empty` / `focused` so the next `filled`
            // re-types from scratch; the schedule resets between loops.
            if (phase === "empty" || phase === "focused") {
                if (typedChars !== 0) setTypedChars(0);
            }
            return undefined;
        }
        const timers: number[] = [];
        for (let i = 1; i <= FULL_SEND_AMOUNT.length; i++) {
            timers.push(window.setTimeout(() => setTypedChars(i), i * TYPE_CHAR_MS));
        }
        return (): void => timers.forEach((t) => window.clearTimeout(t));
        // typedChars is written here, not read for control flow.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase]);
    // Raw user-typed value (no thousands separators) — matches what the
    // user would have keyed into the input. During `filled` it grows a
    // character at a time; from pending onward it's the full value.
    const sendAmount = isPendingOrLater
        ? FULL_SEND_AMOUNT
        : phase === "filled"
          ? FULL_SEND_AMOUNT.slice(0, typedChars)
          : "";

    // ── Scripted CTA hover → press ────────────────────────────────────
    // Once the amount is fully typed (and the quote has settled) the sim
    // plays a hover then a press on the submit Button — so the "click"
    // that advances to pending reads as a deliberate action, not a jump.
    // Forwarded to the Button via `demoSubmitState`. Cleared on every
    // other phase (snapshot/phase styling owns the look from pending on).
    const CTA_HOVER_AT_MS = 950;
    const CTA_PRESS_AT_MS = 1250;
    const [demoSubmitState, setDemoSubmitState] = useState<"idle" | "hover" | "press">("idle");
    useEffect(() => {
        if (phase !== "filled") {
            if (demoSubmitState !== "idle") setDemoSubmitState("idle");
            return undefined;
        }
        const timers: number[] = [];
        timers.push(window.setTimeout(() => setDemoSubmitState("hover"), CTA_HOVER_AT_MS));
        timers.push(window.setTimeout(() => setDemoSubmitState("press"), CTA_PRESS_AT_MS));
        return (): void => timers.forEach((t) => window.clearTimeout(t));
        // demoSubmitState is written here, not read for control flow.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase]);

    // Staggered field-loading inside the `filled` phase. Production
    // shows price-impact almost immediately (computed locally from
    // the route) while the Jupiter quote (receive amount, routing fee)
    // settles ~300–500ms later. We mirror that with three sub-stages
    // so the FeeFooter doesn't snap in fully populated.
    //
    //   stage 0 — initial filled tick: only sendAmount + usdValue
    //             (sell-side) + exchangeRate visible; quote loading.
    //   stage 1 — +200ms: price impact + network cost loaded
    //             (FeeFooter no longer shows a loading state).
    //   stage 2 — +500ms: receive amount + receiveUsdValue +
    //             routing fee loaded. Steady-state filled.
    //
    // Once `pending` fires the snapshot path is authoritative, so we
    // collapse stages here.
    const [filledStage, setFilledStage] = useState<0 | 1 | 2>(0);
    useEffect(() => {
        if (phase !== "filled") {
            if (filledStage !== 0) setFilledStage(0);
            return undefined;
        }
        const t1 = window.setTimeout(() => setFilledStage(1), 200);
        const t2 = window.setTimeout(() => setFilledStage(2), 500);
        return (): void => {
            window.clearTimeout(t1);
            window.clearTimeout(t2);
        };
        // filledStage is set inside, not read for control flow.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase]);
    const quoteReady = (phase === "filled" && filledStage >= 2) || isPendingOrLater;
    const priceImpactReady = (phase === "filled" && filledStage >= 1) || isPendingOrLater;

    // Auto-set cardHeight when sim's auto-trigger advances past
    // `filled`. The widget never calls handleSubmit in sim (the
    // orchestrator's auto-execute fires the schedule directly), so
    // without this the lock would never engage and the card would
    // shrink as content collapses between phases.
    useEffect(() => {
        if (isPendingOrLater && cardHeight === undefined) {
            setCardHeight(SIM_BUY_CARD_HEIGHT);
        } else if (phase === "empty" || phase === "filled") {
            // Clear on reset so a re-run of the schedule starts fresh.
            if (cardHeight !== undefined) setCardHeight(undefined);
        }
    }, [isPendingOrLater, phase, cardHeight]);

    const swapSnapshot =
        phase === "receipt" || phase === "processing" || phase === "success" || phase === "postSuccess"
            ? {
                  // Both sides carry the precision (USDC + USDv both
                  // have 6 decimals) and the thousands separator — this
                  // is the formatted display string used by the
                  // receipt rows, the receipt summary, the big
                  // "Bought" headline, and the TransitionButton label.
                  sendAmount: "10,000.000000",
                  sendSymbol,
                  receiveAmount: "10,000.000000",
                  receiveAmountRaw: "10000.000000",
                  receiveSymbol,
                  sendUsdValue: "10,000.00",
                  receiveUsdValue: "10,000.00",
                  priceImpactDisplay: receiptRows[1].value,
                  routingFeeDisplay: receiptRows[2].value,
                  networkFeeDisplay: receiptRows[3].value
              }
            : null;

    const swapView: SwapView =
        phase === "success" || phase === "postSuccess" ? "success" : isPendingOrLater ? "pending" : "form";

    return {
        mode: side,
        swapView,
        swapSnapshot,
        cardHeight,
        sendAmount,
        // Lets the widget's `useTransactionPhase` report `"focused"`
        // without depending on a real DOM focus event (sim doesn't
        // click into the input). Widget reads via `swap.simIsFocused`
        // and ORs with its own `isInputting`.
        simIsFocused: phase === "focused",
        // Forced hover/press visual for the pre-submit CTA (sim-only).
        demoSubmitState,
        sendCurrency: sendToken.value,
        receiveCurrency: receiveToken.value,
        sendAssetOptions: [sendToken],
        receiveAssetOptions: [receiveToken],
        sendDisplayOption: sendToken,
        receiveDisplayOption: receiveToken,
        // Receive amount gated on quote arrival inside `filled`.
        receiveAmountDisplay: quoteReady ? "10,000.000000" : "",
        // Balance row hidden until the user engages with the input
        // (focused) — production hides Balance until interaction.
        // AmountInput renders `"Balance: {balance}"` with the bare
        // number passed here. The non-zero balance always rides the
        // send leg (the token being spent); the receive leg shows 0.00.
        sendDisplayBalance: isFocusedOrLater ? "152,719.34" : "",
        receiveDisplayBalance: isFocusedOrLater ? "0.00" : "",
        // Sell-side usdValue derives from the *send* token's cached
        // price — available immediately once an amount is entered.
        usdValue: inputHasValue ? "10,000.00" : "",
        // Receive-side usdValue depends on the quote.
        receiveUsdValue: quoteReady ? "10,000.00" : "",
        // Exchange rate (cached oracle price) is always available —
        // shown from `empty` onward. BuyWidget renders
        // `1 ${sendSymbol} = ${exchangeRate} ${receiveSymbol}`.
        exchangeRate: "1.0000",
        // Price impact + network cost are derived locally from the
        // route and resolve before the slower quote endpoint.
        priceImpactDisplay: priceImpactReady ? receiptRows[1].value : "",
        // Routing fee comes from the quote.
        routingFeeDisplay: quoteReady ? receiptRows[2].value : "",
        networkFeeDisplay: priceImpactReady ? receiptRows[3].value : "",
        feeFooterLoading: phase === "filled" && !priceImpactReady,
        isQuoteLoading: phase === "filled" && !quoteReady,
        isQuoteFetching: phase === "filled" && !quoteReady,
        quoteError: null,
        refetchQuote: noop,
        executeState: state,
        ctaFeedback: null,
        isErrorFeedback: phase === "error",
        rejectionCount: 0,
        connected: sim.wallet?.connected ?? true,
        maxBalanceValue: 152719.34,
        canExecuteQuote: phase === "filled" && quoteReady,
        sendCurrencySymbol: sendSymbol,
        receiveCurrencySymbol: receiveSymbol,
        sendTokenDecimals: 6,
        receiveTokenDecimals: 6,
        handleSendAmountChange: noopValueSet,
        handleBlur: noop,
        handleMaxClick: noop,
        handleSwapClick: noop,
        handleBuyTab: noop,
        handleSellTab: noop,
        handleCurrencyChange: noop,
        handleSuccessClose: reset,
        handleSubmit,
        handlePendingCancel: reset,
        setSendSelectedOption: noopOptionSet,
        setReceiveSelectedOption: noopOptionSet
    };
}
